import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  UiImageCarouselImage,
  UiImageCarouselImageInput,
  UiImageCarouselIndexChange,
} from './image-carousel.types';

export type {
  UiImageCarouselImage,
  UiImageCarouselImageInput,
  UiImageCarouselIndexChange,
};

function toSlide(item: UiImageCarouselImageInput): UiImageCarouselImage {
  return typeof item === 'string' ? { src: item } : item;
}

/**
 * Organismo: carrusel de imágenes a ancho completo.
 *
 * Navegación circular con flechas e indicadores. Es presentacional.
 * Flechas y swipe empujan el slide actual fuera del marco.
 * Si el slide trae `wideSrc` / `compactSrc`, elige el arte con `<picture>`.
 */
@Component({
  selector: 'ui-image-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-carousel.component.html',
  styleUrl: './image-carousel.component.scss',
  host: { class: 'ui-image-carousel-host' },
})
export class UiImageCarouselComponent implements OnDestroy {
  readonly images = input<readonly UiImageCarouselImageInput[]>([]);
  readonly altPrefix = input('Promoción');

  readonly indexChange = output<UiImageCarouselIndexChange>();

  protected readonly activeIndex = signal(0);
  protected readonly incomingIndex = signal<number | null>(null);
  protected readonly direction = signal<'next' | 'prev'>('next');
  protected readonly pushing = signal(false);
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeFromControl = false;
  private pushFrame = 0;

  /**
   * Misma escala que `_breakpoints.scss` (`media-down` / `media-short`).
   * `<source media>` no puede usar mixins SCSS.
   */
  protected readonly shortMedia =
    '(max-height: 540px) and (orientation: landscape)';
  protected readonly compactMedia = '(max-width: 767.98px)';
  protected readonly wideMedia = '(max-width: 1023.98px)';

  protected readonly slides = computed(() => this.images().map(toSlide));

  protected readonly frames = computed(() => {
    const slides = this.slides();
    const currentIndex = this.normalizedIndex();
    const current = slides[currentIndex];
    if (!current) return [];
    const incomingIndex = this.incomingIndex();
    const incoming = incomingIndex == null ? null : slides[incomingIndex];
    if (!incoming || incomingIndex == null) {
      return [{ slide: current, index: currentIndex }];
    }
    const incomingFrame = { slide: incoming, index: incomingIndex };
    const currentFrame = { slide: current, index: currentIndex };
    return this.direction() === 'prev'
      ? [incomingFrame, currentFrame]
      : [currentFrame, incomingFrame];
  });

  ngOnDestroy(): void {
    cancelAnimationFrame(this.pushFrame);
  }

  protected setActiveIndex(index: number): void {
    const total = this.slides().length;
    if (!total || this.incomingIndex() !== null) return;
    const next = ((index % total) + total) % total;
    if (next === this.normalizedIndex()) return;
    this.pushTo(next, next > this.normalizedIndex() ? 'next' : 'prev');
  }

  protected showPrevious(): void {
    this.pushBy(-1);
  }

  protected showNext(): void {
    this.pushBy(1);
  }

  protected onPushEnd(event: TransitionEvent): void {
    if (
      event.propertyName !== 'transform' ||
      event.target !== event.currentTarget
    ) {
      return;
    }
    const incoming = this.incomingIndex();
    if (incoming == null || !this.pushing()) return;
    this.pushing.set(false);
    this.incomingIndex.set(null);
    this.commitIndex(incoming);
  }

  protected onSwipeStart(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    this.swipeFromControl = !!target?.closest('button');
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  protected onSwipeEnd(event: PointerEvent): void {
    if (this.swipeFromControl) return;
    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (deltaX < 0) this.showNext();
    else this.showPrevious();
  }

  protected normalizedIndex(): number {
    const total = this.slides().length;
    if (!total) return 0;
    return ((this.activeIndex() % total) + total) % total;
  }

  private pushBy(delta: 1 | -1): void {
    const total = this.slides().length;
    if (total < 2 || this.incomingIndex() !== null) return;
    const next = (this.normalizedIndex() + delta + total) % total;
    this.pushTo(next, delta > 0 ? 'next' : 'prev');
  }

  private pushTo(index: number, direction: 'next' | 'prev'): void {
    cancelAnimationFrame(this.pushFrame);
    this.direction.set(direction);
    this.pushing.set(false);
    this.incomingIndex.set(index);
    this.pushFrame = requestAnimationFrame(() => {
      this.pushFrame = requestAnimationFrame(() => {
        if (this.incomingIndex() == null) return;
        this.pushing.set(true);
      });
    });
  }

  private commitIndex(index: number): void {
    const slides = this.slides();
    if (!slides.length) return;
    const next = ((index % slides.length) + slides.length) % slides.length;
    this.activeIndex.set(next);
    const slide = slides[next];
    if (slide) this.indexChange.emit({ index: next, src: slide.src });
  }
}
